import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { forwardRef, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
/**
 * an hoc to rerender component only when they are in view or batched re render
 * TODO: Add useDeferredValue once migrated to react 18.
 * Manual throtlling state using setState in timeout may have reverse effect, as
 * the setStates are not batched together when triggered inside set timeout.
 * Also, in case there is just one rerender, throttling would yield 2 rerender
 */
export function withOptimizedRerender(Component, options = {}) {
    const { inViewOptimization = true, intersectionOption, useContextState = () => ({}) } = options;
    const MemoizedComponent = React.memo(Component, ({ inView: prevInView }, { inView: nextInView }) => {
        // return true in case of element is outside of view,
        return !nextInView && nextInView === prevInView;
    });
    return (props) => {
        const { ref: containerRef, inView } = useInView(intersectionOption);
        // if (props.source) console.debug(props, inView);
        // get context state to memoize it
        const contextState = useContextState(props, inView);
        const _inView = inViewOptimization ? inView : true;
        return (_jsx(MemoizedComponent, Object.assign({}, props, { containerRef: containerRef, inView: _inView, contextState: contextState })));
    };
}
export const VirtualizedContainer = forwardRef((_a, ref) => {
    var { as, threshold, rootMargin, minHeight, style, children, onChange } = _a, restProps = __rest(_a, ["as", "threshold", "rootMargin", "minHeight", "style", "children", "onChange"]);
    const Component = as || 'div';
    const { ref: containerRef, entry, inView } = useInView({ threshold, rootMargin, onChange });
    const refSetter = useCallback((element) => {
        if (typeof containerRef === 'function') {
            containerRef(element);
        }
        else if (ref) {
            containerRef.current = element;
        }
        if (typeof ref === 'function') {
            ref(element);
        }
        else if (ref) {
            ref.current = element;
        }
    }, [containerRef, ref]);
    const height = !inView && entry ? entry.boundingClientRect.height : style === null || style === void 0 ? void 0 : style.height;
    const _minHeight = (style === null || style === void 0 ? void 0 : style.minHeight) || minHeight;
    /**
     * Render children only when in view for performance optimization
     * while the element is going out of view calculate its current height, and set that height in the container,
     * so scroll jump doesn't happen on subsequent elements.
     */
    return (_jsx(Component, Object.assign({ ref: refSetter, style: Object.assign(Object.assign({}, style), { height: height, minHeight: _minHeight }) }, restProps, { children: inView ? children : null })));
});
